import React, { useCallback, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import ReactTable from "react-table";
import { PageTitle } from '../PageTitle';
import { EntityService, PastChecklistsService, TimeFormattingService, UserUnitService } from '../../services';
import { ChecklistImage } from '../Checklist';
import i18next from "i18next";
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';
import SimpleLineGraph from '../Graphs/simple_line_graph';
import moment from 'moment-timezone';
import { sprintf } from 'sprintf-js';

const MATCH_HEADER_BOLD = { fontWeight: "500" };

const CorrectiveActionTask = ({ correctiveAction, correctiveActions }) => {
  let correctiveActionOptions = correctiveAction.pastChecklistTaskOptions.toSorted((a, b) => (a.checklistTaskConfig?.taskOrder - b.checklistTaskConfig?.taskOrder));

  return (
    <div key={correctiveAction.id}>
      <br/> <b>Corrective Actions:</b>  <br/>
      <ul> {
        correctiveActionOptions.map((option) => 
          (
            <li key={option.id}>
              { option.type === 'photo' && 
                <div>
                  {option.checklistTaskOptionConfig.name.trim()}:&nbsp;
                  { option.pastChecklistTaskPhotos?.length == 0 ?
                    <span style={MATCH_HEADER_BOLD}>Skipped</span> :
                    <PhotosTask pastChecklistTaskPhotos={option.pastChecklistTaskPhotos} />
                  }
                </div> 
              }
              { option.type === 'selection' && 
                <div>
                  {option.checklistTaskOptionConfig.name.trim()}:&nbsp;
                  <SelectionsTask option={option} correctiveActions={correctiveActions} />
                </div> 
              }
              { option.type === 'temperature' && 
                <div>
                  {option.checklistTaskOptionConfig.name.trim()}:&nbsp;
                  <TempTask option={option} correctiveActions={correctiveActions} />
                </div> 
              }
              { option.type === 'note' && 
                <div>
                  {option.checklistTaskOptionConfig.name.trim()}:&nbsp;
                  { option.note == null ?
                    <span style={MATCH_HEADER_BOLD}>Skipped</span> :
                    <span style={MATCH_HEADER_BOLD}>{option.note}</span>
                  }
                </div> 
              }
            </li>
          )
        )}
      </ul>
    </div>
  );
};

const PassiveTempTaskReadingsModal = ({ serialNumber, startTimestamp, endTimestamp, show, onHide, timezone }) => {
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState(null);
  const [data, setData] = useState(null);
  const [lastReadingTime, setLastReadingTime] = useState(null);

  useEffect(() => {
    if (show === true) {
      setLoading(true);
      EntityService.getBySerialNumber(serialNumber).then(({ entity }) => {
        setEntity(entity);

        const finalEndTimestamp = endTimestamp || new Date().getTime();
        EntityService.getMeasurementsForRange('temperature', serialNumber, startTimestamp, finalEndTimestamp).then(({ data }) => {
          if (data.length > 0) {
            setData(data.toSorted((a, b) => (a.timestamp - b.timestamp)));
            setLastReadingTime(TimeFormattingService.renderNormalTime(moment(data[data.length-1].timestamp).tz(timezone)));
          } else {
            setData([]);
            setLastReadingTime("NA")
          }
          setLoading(false);
        }).catch((error) => {
          console.error(error);
          setLoading(false);
        });
      }).catch((error) => {
        console.error(error);
        setLoading(false);
      })
    }
  }, [serialNumber, startTimestamp, endTimestamp, show]);

  const refreshGraph = useCallback(() => {
    setLoading(true);
    const finalEndTimestamp = endTimestamp || new Date().getTime();
    EntityService.getMeasurementsForRange('temperature', serialNumber, startTimestamp, finalEndTimestamp).then(({ data }) => {
      setData(data.toSorted((a, b) => (a.timestamp - b.timestamp)));
      setLastReadingTime(TimeFormattingService.renderNormalTime(moment(data[data.length-1].timestamp).tz(timezone)));
      setLoading(false);
    }).catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [serialNumber, startTimestamp, endTimestamp]);

  return (
    <Modal show={show} onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title>Readings {entity && <span>for {entity.displayName}</span>}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          { loading && <div className="loader">Loading....</div>}
          {
            !loading && entity && data && 
            <div style={{padding: 10}}>
              <div className='row'>
                <div className='col-sm-10' style={{paddingTop:20, paddingLeft:10, paddingBottom:10}}>
                  <strong>Data as of {lastReadingTime}</strong>
                </div>
                {endTimestamp == null && <div className='col-sm-2'>
                  <a onClick={refreshGraph}><i className="op-icon op-icon-refresh" /></a>
                </div>}
              </div>
              <div className='row'>
                { data.length == 0 ?
                  <div>No readings available</div> :
                  <SimpleLineGraph rawData={data} dataTypeLabel='Temperature' timezone={timezone} displayName={entity.displayName} />
                }
              </div>
            </div>
          }
        </Modal.Body>
        <Modal.Footer>
          <a className="btn btn-default" onClick={onHide}>Close</a>
        </Modal.Footer>
    </Modal>
  );
};

const PassiveTempTaskReadings = ({ serialNumber, startTimestamp, endTimestamp, timezone }) => {
  const [showReadingsModal, setShowReadingsModal] = useState(false);

  return (
    <span>
      <PassiveTempTaskReadingsModal 
        serialNumber={serialNumber}
        startTimestamp={startTimestamp} 
        endTimestamp={endTimestamp} 
        timezone={timezone}
        show={showReadingsModal} 
        onHide={() => setShowReadingsModal(false)}
      />
      <button className='btn btn-default' onClick={() => setShowReadingsModal(true)}>View Readings</button>
    </span>
  );
};

const runtimeCalculator = (startedAt, completedAt) => {
  const runtime = completedAt - startedAt;
  const runtimeInMinutes = (runtime / 1000 / 60).toFixed(0);
  return sprintf("%02d:%02d", Math.trunc(runtimeInMinutes / 60), runtimeInMinutes % 60);
};

const TempTask = connect(({ user }) => ({ me: user.me }))(({ me, timezone, option, correctiveActions, aborted }) => {
  const config = option.checklistTaskOptionConfig;
  
  if (config.passiveContinuousReading) {
    const startedAtLine = option.passiveStartedAt ? TimeFormattingService.renderNormalTime(option.passiveStartedAt, timezone) : "Not Started";
    const startedByLine = option.passiveStartedBy ? `by ${option.passiveStartedBy}` : '';
    const completedAtLine = option.passiveCompletedAt ? TimeFormattingService.renderNormalTime(option.passiveCompletedAt, timezone) : "Not Completed";
    const completedByLine = option.passiveCompletedBy ? `by ${option.passiveCompletedBy}` : "";
    const runtimeLine = aborted ? "Abandoned" :
        (option.passiveStartedAt ? runtimeCalculator(option.passiveStartedAt, option.passiveCompletedAt || new Date().getTime()) : null);

    return (
      <div>
        <table>
          <tbody>
            <tr><td style={{textAlign:'right'}}>Started At:</td><td style={{paddingLeft:10}}><strong>{startedAtLine} {startedByLine}</strong></td></tr>
            <tr><td style={{textAlign:'right'}}>Completed At:</td><td style={{paddingLeft:10}}><strong>{completedAtLine} {completedByLine}</strong></td></tr>
            { runtimeLine && 
              <tr><td style={{textAlign:'right'}}>Runtime:</td><td style={{paddingLeft:10}}><strong>{runtimeLine}</strong></td></tr>
            }
          </tbody>
        </table>
        { !aborted && option.passiveStartedAt && <div>
            &nbsp;&nbsp; <PassiveTempTaskReadings serialNumber={option.deviceSerialNumber} startTimestamp={option.passiveStartedAt} endTimestamp={option.passiveCompletedAt} timezone={timezone} />
          </div> }
      </div>
    );
  } else {
    const temp = option.temperature;

    if (temp || temp === 0) {
      let correctiveAction;
      if (option.checklistTaskOptionConfig.constraintCorrectiveAction) {
        let constraintCorrectiveAction = correctiveActions?.get(option.checklistTaskOptionConfig.constraintCorrectiveAction.id);
        if (constraintCorrectiveAction) {
            correctiveAction = (<CorrectiveActionTask correctiveAction={constraintCorrectiveAction} />);
        }
      }

      return (
        <span>
          <span style={MATCH_HEADER_BOLD}>{ sprintf("%0.2f° %s", UserUnitService.convertFromSystemUnitToUserUnit(me.temperatureDisplayUnit, temp), me.temperatureDisplayUnit) }</span>
          {!option.takenByDevice && <span> (manual entry)</span>}
          {correctiveAction}
        </span>
      );
    } else {
      return i18next.t("na");
    }
  }
});

const SelectionsTask = ({ option, correctiveActions }) => {
  if (option.pastChecklistTaskSelectedOptions && option.pastChecklistTaskSelectedOptions.length > 0) {
    return (
      <span>
        <span style={MATCH_HEADER_BOLD}>
          {option.pastChecklistTaskSelectedOptions.map(selection => selection.checklistTaskSelectionOption.name).join(', ')}
        </span>  
        {
          option.pastChecklistTaskSelectedOptions.map((selection) => {
            if (selection.checklistTaskSelectionOption.correctiveAction) {
              const correctiveActionTask = correctiveActions?.get(selection.checklistTaskSelectionOption.correctiveAction.id);
              if (correctiveActionTask) {
                return <CorrectiveActionTask key={selection.id} correctiveAction={correctiveActionTask} correctiveActions={correctiveActions} />;
              }
            }
          })
        }
      </span>
    );
  } else {
    return (<span>{ i18next.t('na') }</span>);
  }
};

const PhotosTask = ({ pastChecklistTaskPhotos }) => (
  pastChecklistTaskPhotos.map((photo, index) => <div key={index}><ChecklistImage imgUrl={photo.photoURL}/></div>)
);

const TaskOptions = ({ pastChecklistTaskOptions, timezone, correctiveActions, failureCorrectionActionTask, aborted }) => (
  <div className="col-sm-12"> 
  {
    pastChecklistTaskOptions
        .map((option) => (
          <div key={option.id} className="row">
          { option.type === 'photo' && <span>{option.checklistTaskOptionConfig.name}: <div><PhotosTask pastChecklistTaskPhotos={option.pastChecklistTaskPhotos} /></div></span> }
          { option.type === 'selection' && <span>{option.checklistTaskOptionConfig.name}: <SelectionsTask option={option} correctiveActions={correctiveActions} /></span> }
          { option.type === 'temperature' && <span>{option.checklistTaskOptionConfig.name}: <TempTask timezone={timezone} option={option} correctiveActions={correctiveActions} aborted={aborted} /></span> }
          { option.type === 'note' && <span>{option.checklistTaskOptionConfig.name}: <span style={MATCH_HEADER_BOLD}>{option.note}</span></span> }
          </div>
        ))
  }
  { failureCorrectionActionTask && <div key={failureCorrectionActionTask.id} className="row"><CorrectiveActionTask correctiveAction={failureCorrectionActionTask} /></div> }
  </div>
);

const ChecklistTaskList = ({ tasks, timezone, correctiveActions, aborted }) => (
    <div className="react-table-custom">
        <ReactTable
            className="-striped -highlight"
            data={tasks}
            showPagination={false}
            pageSize={tasks.length}
            columns={[
                {
                    Header: i18next.t("table-headers.task"),
                    accessor: "checklistTaskConfig.name",
                    sortable: false,
                    minResizeWidth: 50,
                },
                {
                    Header: i18next.t("table-headers.group"),
                    id: "group",
                    accessor: (row) => row.checklistTaskConfig.checklistTaskGroup?.name ?? "",
                    sortable: false,
                    minResizeWidth: 30,
                },
                {
                    Header: i18next.t("table-headers.steps"),
                    id: "pastChecklistTaskOptions",
                    Cell: (cell) => (
                        <TaskOptions
                            key={cell.index}
                            timezone={timezone}
                            pastChecklistTaskOptions={cell.original.pastChecklistTaskOptions}
                            correctiveActions={correctiveActions}
                            failureCorrectionActionTask={cell.original.failureCorrectionActionTask}
                            aborted={aborted}
                        />
                    ),
                    style: { whiteSpace: "unset" },
                    sortable: false,
                    minWidth: 250,
                },
                {
                    Header: "Time",
                    id: "startedAt",
                    accessor: (row) => (
                        <div>
                            {row.startedAt ? TimeFormattingService.renderNormalTime(row.startedAt, timezone) : "Not Started"} 
                            {row.startedAt && <span><br /> to <br /></span>}
                            {row.startedAt && (row.completedAt ? TimeFormattingService.renderNormalTime(row.completedAt, timezone) : "Not Completed")}
                        </div>
                    ),
                    sortable: false,
                    minWidth: 120,
                },
            ]}
        />
    </div>
);

const ChecklistDetailHeader = ({ caCompletedEndTime, startTime, endTime, timezone, completedByName, caCompletedByName, correctiveActionsRequired }) => (
  <div className="op-content-box">
    <div className="row">
      <div className="col-sm-4 form-group">
        <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-description">
          <label>{ i18next.t('pages.checklists.start-time') }</label>
          <p>{TimeFormattingService.renderNormalTime(startTime, timezone)}</p>
        </div>
      </div>
      <div className="col-sm-4 form-group">
        <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-description">
          <label>{ i18next.t('pages.checklists.end-time') }</label>
          <p>{endTime ? TimeFormattingService.renderNormalTime(endTime, timezone) : 'Not Ended'}</p>
        </div>
      </div>
      <div className="col-sm-4 form-group">
        <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-name">
        <label>{ i18next.t('pages.checklists.completed-by') }</label>
        <p>{completedByName}</p>
        </div>
      </div>
    </div>
    {correctiveActionsRequired &&
    <div className="row">
      <div className="col-sm-4 form-group">
      </div>
      <div className="col-sm-4 form-group">
        <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-description">
          <label>Corrective Actions Completed At</label>
          <p>{caCompletedEndTime ? TimeFormattingService.renderNormalTime(caCompletedEndTime, timezone) : 'Not Completed'}</p>
        </div>
      </div>
      <div className="col-sm-4 form-group">
        <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-name">
        <label>Corrective Actions Completed By</label>
        <p>{caCompletedByName}</p>
        </div>
      </div>
    </div>
    }
  </div>
);

const ChecklistDetail = ({ checklist, correctiveActions, failureCorrectionActionTask, timezone }) => {
  const totalTasks = checklist.checklistConfig.checklistTaskConfigs.filter((task) => !task.correctiveAction);
  const completedTasks = checklist.pastChecklistTasks.filter((task) => task.completedAt);
  const pctComplete = totalTasks.length > 0 ? (completedTasks.length / totalTasks.length) * 100 : 0;
  const completedByName = checklist.endTime ? (checklist.completedByUser?.firstName ?? checklist.completedByChecklistUser?.fullName ?? '') : 'Not Ended';
  const caCompletedByName = checklist.caCompletedEndTime ? checklist.caCompletedByUser?.firstName ?? checklist.caCompletedByChecklistUser?.fullName ?? "" : "Not Completed";

  const [pastChecklistTasks, setPastChecklistTasks] = useState(null);
  
  useEffect(() => {
    let pastChecklistTasks = [];
    
    checklist.checklistConfig.checklistTaskConfigs.forEach((taskConfig) => {
      const pastChecklistTask = checklist.pastChecklistTasks.find((pct) => pct.checklistTaskConfig.id == taskConfig.id);
      if (pastChecklistTask) {
        if (pastChecklistTask.startedAt != null && pastChecklistTask.completedAt == null && failureCorrectionActionTask) {
          pastChecklistTasks.push({...pastChecklistTask, failureCorrectionActionTask: failureCorrectionActionTask});
        } else {
          pastChecklistTasks.push(pastChecklistTask);
        }
      } else {
        pastChecklistTasks.push({
          checklistTaskConfig: taskConfig,
          completedAt: null,
          failedAt: null,
          startedAt: null,
          pastChecklistTaskOptions: []
        });
      }
    });

    setPastChecklistTasks(pastChecklistTasks.toSorted((a, b) => (a.checklistTaskConfig.taskOrder - b.checklistTaskConfig.taskOrder)));
  }, [checklist]);

  return (
      <div style={{paddingLeft: 30, paddingRight: 30}}>
        <ChecklistDetailHeader 
          startTime={checklist.startTime} 
          endTime={checklist.endTime} 
          caCompletedEndTime={checklist.caCompletedEndTime} 
          timezone={timezone} 
          completedByName={completedByName} 
          caCompletedByName={caCompletedByName}
          correctiveActionsRequired={checklist.correctiveActionsRequired}
        />

        <div className="op-content-box">
          <h4 className="remove-margin-bottom">
            { i18next.t('pages.checklists.completed-tasks') } <span>{completedTasks.length} {i18next.t('of')} {totalTasks.length} ({pctComplete.toFixed(0)}%)</span>
          </h4> <br/>

          {pastChecklistTasks && <ChecklistTaskList 
            tasks={pastChecklistTasks} 
            correctiveActions={correctiveActions} 
            timezone={timezone} 
            aborted={checklist.aborted === true} 
          />}
        </div>
      </div>
  );
}

const MonitorChecklistDetail = ({ location, match, history }) => {
  const [checklist, setChecklist] = useState(null);
  const [correctiveActions, setCorrectiveActions] = useState(null);
  const [failureCorrectionActionTask, setFailureCorrectionActionTask] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const groupingId = match.params.deviceGroupingId;

  const loadData = useCallback((checklistId) => {
    setChecklist(null);
    PastChecklistsService.get(checklistId, true).then((response) => { 
          const checklist = response.checklist;        
          let correctiveActions = new Map();
          checklist.pastChecklistTasks.forEach((task) => {
            if (task.checklistTaskConfig.correctiveAction) {
              correctiveActions.set(task.checklistTaskConfig.id, task);
            }
          });

          if (checklist.checklistConfig.failureCorrectiveAction) {
            setFailureCorrectionActionTask(checklist.pastChecklistTasks.find(task => task.checklistTaskConfig.id == checklist.checklistConfig.failureCorrectiveAction.id));
          }

          checklist.pastChecklistTasks = checklist.pastChecklistTasks.filter(task => !task.checklistTaskConfig.correctiveAction);

          setChecklist(checklist);
          setCorrectiveActions(correctiveActions);
          setTimezone(checklist.deviceGrouping ? checklist.deviceGrouping.timezone : null);   
          
          if (checklistId != checklist.id) {
            history.push({
              pathname: `/monitoring/locations/268/checklists/${checklist.id}`,
              state: { checklist: checklist }
            });
          }
        }).catch((error) => {
          setErrorMessage(i18next.t('messages.could-not-load-checklist'));
          console.error(error);
        });
  }, [history]);

  useEffect(() => {
    if (location.state?.checklist) {
      setChecklist(location.state?.checklist);
    } else {
      loadData(match.params.checklistId);
    }
  }, [match]);

  if (errorMessage) {
    return (<Redirect to={{ pathname: `/monitoring/locations/${groupingId}/checklists`, state: { errorMessage: errorMessage } }}/>);
  } else if (checklist) {

    return (
      <div>
        <div className="row">
          <PageTitle 
            title={`${checklist.checklistConfig.name} (V${checklist.checklistConfig.version})`} 
            backLinkWithParams={{ link: { pathname: `/monitoring/locations/${groupingId}/checklists`, search: location.state?.backLinkSearch, state: location.state } }}
            refreshFunction={() => loadData(match.params.checklistId)}
          />
        </div>

        <ChecklistDetail 
          checklist={checklist} 
          correctiveActions={correctiveActions} 
          failureCorrectionActionTask={failureCorrectionActionTask}
          timezone={timezone} 
        />

      </div>
    );
    
  } else {
    return (<div className="loader">{ i18next.t('loading') }</div>);
  }
}

export default MonitorChecklistDetail;
